const GALLERY_SELECTOR = 'media-gallery';
const ENHANCED_CLASS = 'fc-gallery-enhanced';

function setSelected(buttons, index) {
  buttons.forEach((button, buttonIndex) => {
    button.setAttribute('aria-selected', String(buttonIndex === index));
    button.tabIndex = buttonIndex === index ? 0 : -1;
  });

  const selectedButton = buttons[index];
  selectedButton?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
}

function createThumbnail(slide, index, slideshow, buttons) {
  const preview = slide.querySelector('.product-media__image, .deferred-media__poster-image, img');
  if (!(preview instanceof HTMLImageElement)) return null;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'slideshow-control button button-unstyled slideshow-controls__thumbnail fc-gallery-thumbnail';
  button.setAttribute('aria-label', `Selectează imaginea ${index + 1}`);
  button.setAttribute('aria-selected', String(index === 0));
  button.setAttribute('ref', 'thumbnails[]');
  button.tabIndex = index === 0 ? 0 : -1;

  const image = document.createElement('img');
  image.src = preview.currentSrc || preview.src;
  if (preview.srcset) image.srcset = preview.srcset;
  image.sizes = '64px';
  image.alt = preview.alt || `Imagine produs ${index + 1}`;
  image.loading = 'lazy';
  image.decoding = 'async';
  button.append(image);

  if (slide.matches('.product-media-container--video, .product-media-container--external_video')) {
    const badge = document.createElement('span');
    badge.className = 'slideshow-controls__thumbnail-badge fc-gallery-video-badge';
    badge.setAttribute('aria-hidden', 'true');
    badge.textContent = '▶';
    button.append(badge);
  }

  button.addEventListener('click', (event) => {
    if (typeof slideshow.select === 'function') {
      slideshow.select(index, event);
    }
    setSelected(buttons, index);
  });

  return button;
}

function enhanceGallery(gallery) {
  if (!(gallery instanceof HTMLElement)) return;
  if (gallery.classList.contains(ENHANCED_CLASS)) return;
  if (!gallery.closest('.product-information')) return;

  const slideshow = gallery.querySelector('slideshow-component');
  const scroller = slideshow?.querySelector('slideshow-slides');
  if (!(slideshow instanceof HTMLElement) || !(scroller instanceof HTMLElement)) return;

  const slides = Array.from(scroller.children).filter((element) => element.matches('slideshow-slide'));
  if (slides.length < 2) return;

  const controls = document.createElement('slideshow-controls');
  controls.className = 'fc-gallery-thumbnails';
  controls.setAttribute('pagination-position', 'left');
  controls.setAttribute('scroll-mode', 'vertical');
  controls.setAttribute('thumbnails', '');
  controls.style.setProperty('--thumbnail-width', '64px');

  const scrollHint = document.createElement('scroll-hint');
  scrollHint.className = 'slideshow-controls__thumbnails-container';
  scrollHint.setAttribute('ref', 'thumbnailsContainer');

  const list = document.createElement('div');
  list.className = 'slideshow-controls__thumbnails';

  const buttons = [];
  slides.forEach((slide, index) => {
    const button = createThumbnail(slide, index, slideshow, buttons);
    if (!button) return;
    buttons.push(button);
    list.append(button);
  });

  if (buttons.length < 2) return;

  scrollHint.append(list);
  controls.append(scrollHint);

  const originalControls = slideshow.querySelector(':scope > slideshow-controls');
  if (originalControls) {
    slideshow.insertBefore(controls, originalControls);
  } else {
    slideshow.append(controls);
  }

  const initialIndex = Number.isInteger(slideshow.current) ? slideshow.current : 0;
  setSelected(buttons, initialIndex);

  slideshow.addEventListener('slideshow:select', (event) => {
    const index = Number(event.detail?.index ?? 0);
    setSelected(buttons, index);
  });

  gallery.classList.add(ENHANCED_CLASS);
  gallery.dataset.fcGalleryEnhanced = 'true';
}

function enhanceAllGalleries(root = document) {
  root.querySelectorAll?.(GALLERY_SELECTOR).forEach(enhanceGallery);
  if (root.matches?.(GALLERY_SELECTOR)) enhanceGallery(root);
}

let scheduledFrame;
function scheduleEnhancement(root = document) {
  cancelAnimationFrame(scheduledFrame);
  scheduledFrame = requestAnimationFrame(() => enhanceAllGalleries(root));
}

function boot() {
  scheduleEnhancement(document);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) scheduleEnhancement(node);
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

Promise.all([
  customElements.whenDefined('slideshow-component'),
  customElements.whenDefined('media-gallery'),
]).then(() => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
});
